/**
 * Dictionary vocabulary.
 *
 * Alphabet and number diagrams are generated by
 * `training/mudra_train/export/diagrams.py` from the project's own kinematic hand
 * model, so they carry the repository's MIT licence with no attribution burden
 * (docs/AUDIT.md, A1).
 *
 * Only the fingerspelling vocabulary is listed — the categories the recogniser can
 * actually read and the Dictionary can actually illustrate. The former word categories
 * (greetings, family, …) had neither artwork nor a recognition model behind them and
 * were removed rather than shown empty; they return with a word-level pack and PopSign
 * clips (CC BY 4.0).
 */

export interface SignCategory {
  readonly id: string;
  readonly label: string;
  readonly signs: readonly string[];
}

export const CATEGORIES: readonly SignCategory[] = [
  {
    id: 'alphabets',
    label: '🔤 Alphabets',
    signs: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
  },
  {
    id: 'numbers',
    label: '🔢 Numbers',
    signs: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
  },
];

/**
 * Image formats tried in order before falling back to the placeholder.
 *
 * SVG first: the generated diagrams are vector, so they stay sharp at any size and
 * cost a few kilobytes each. Raster formats remain as fallbacks for categories that
 * later gain photographs or video stills.
 */
export const IMAGE_FORMATS = ['svg', 'jpg', 'png', 'webp'] as const;

/** Path to a sign's illustration, for a given format. */
export function signImagePath(categoryId: string, sign: string, format: string): string {
  return `${categoryId}/${sign.toLowerCase().replace(/ /g, '_')}.${format}`;
}
